import { match, Default } from '@nike/rcf-fp';
import { format, subMonths, parseISO } from 'date-fns';
import React, { useEffect, useState } from 'react';

import TillVarianceSummaryForm from './TillVarianceSummaryForm';
import TillVarianceSummaryTable from './TillVarianceSummaryTable';
import { useGetStoreInfo } from '../../../../globals';
import { useGetLocalization } from '../../../../hooks';
import { getTillVarianceSummaries } from '../../../../utils/service-calls';
import { getLocalEndDateToUTC, getLocalStartDateToUTC } from '../../../../utils/timeUtils';
import { ErrorMessage, Subheader } from '../../../reusable';

const DATE_FORMAT = 'yyyy-MM-dd';

const formatDate = (date, dateFormat = DATE_FORMAT) => format(date, dateFormat);

const formatCurrency = (amount, currency) =>
  new Intl.NumberFormat(undefined, { currency, style: 'currency' }).format(amount);
const getAthleteInfo = ({ athleteName, athleteNumber }) => `${athleteName} (${athleteNumber})`;
const getLocaleDate = ({ createTime }) => formatDate(parseISO(createTime), 'yyyy-MM-dd HH:mm');
const getLocaleAmountExpected = ({ amountExpected, tenderType, subTenderType }, currency) =>
  formatCurrency(amountExpected, tenderType === 'CASH' ? subTenderType : currency);
const getLocaleAmountReceived = ({ amountReceived, tenderType, subTenderType }, currency) =>
  formatCurrency(amountReceived, tenderType === 'CASH' ? subTenderType : currency);
const getLocaleVariance = ({ variance, tenderType, subTenderType }, currency) =>
  formatCurrency(variance, tenderType === 'CASH' ? subTenderType : currency);
const getVarianceExplanation = ({ varianceExplanation }) => `${varianceExplanation}`;
const getVarianceColor = ({ variance }) =>
  match(variance)(
    [() => variance, '#DD6666'], // Red
    [Default, '#111'], // Black
  );

const getCurrencySymbol = (currencyCode) => {
  return (0)
    .toLocaleString(undefined, {
      style: 'currency',
      currency: currencyCode,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    })
    .replace(/\d/g, '')
    .trim();
};

const processTenders = (tenders) => {
  // Group tenders by subTenderType or tenderType
  const groupedTenders = tenders.reduce((acc, tender) => {
    const key = tender.subTenderType || tender.tenderType;
    const displayKey = tender.tenderType === 'CASH' ? `${getCurrencySymbol(key)} ${key}` : key;
    return {
      ...acc,
      [displayKey]: [...(acc[displayKey] || []), tender],
    };
  }, {});

  // Convert the grouped tenders object to an array
  const tendersBySubTenderType = Object.keys(groupedTenders).map((key) => ({
    subTenderType: key,
    tenders: groupedTenders[key],
  }));

  // Flatten the array to have the first element of each group as the subTenderType or tenderType
  const flattenedTenders = tendersBySubTenderType.flatMap((group) => [
    group.subTenderType,
    ...group.tenders,
  ]);

  return flattenedTenders;
};

const addLocalizationToTillVarianceSummary = (currency) => (tillVarianceSummary) => ({
  ...tillVarianceSummary,
  athleteInfo: getAthleteInfo(tillVarianceSummary),
  localeDate: getLocaleDate(tillVarianceSummary),
  localeAmountReceived: getLocaleAmountReceived(tillVarianceSummary, currency),
  localeAmountExpected: getLocaleAmountExpected(tillVarianceSummary, currency),
  varianceExplanation: tillVarianceSummary.varianceExplanation
    ? getVarianceExplanation(tillVarianceSummary)
    : '',
  localeVariance: tillVarianceSummary.variance
    ? getLocaleVariance(tillVarianceSummary, currency)
    : '',
  varianceReason: tillVarianceSummary.varianceReason ?? '',
  varianceColor: getVarianceColor(tillVarianceSummary),
  tillNumber: tillVarianceSummary.tillNumber,
});

function TillVarianceSummary() {
  const getLocalization = useGetLocalization();
  const { currency } = useGetStoreInfo();

  // Contextual state
  const now = new Date();
  const today = formatDate(now);
  const maxDate = today; // Can't be a future date
  const minDate = formatDate(subMonths(now, 13)); // Can't be more than 13 months in the past;

  // React state
  const [startDate, setStartDate] = useState(today);
  const [endDate, setEndDate] = useState(today);
  const [formState, setFormState] = useState('INITIAL'); // INITIAL, SUBMITTING, SUBMIT_SUCCESS, SUBMIT_ERROR
  const [tenders, setTenders] = useState([]);
  const [submissionError, setSubmissionError] = useState('');

  useEffect(() => {
    const resetTillVarianceSummaries = () => {
      setSubmissionError('');
      setTenders([]);
    };
    const handleSubmitSuccess = (data) => {
      setFormState('SUBMIT_SUCCESS');

      const processedTenders = processTenders(
        data.tenders
          .filter(({ tenderType, isEditable }) => tenderType === 'CASH' || isEditable)
          .map(addLocalizationToTillVarianceSummary(currency)),
      );

      setTenders(processedTenders);
    };
    const handleSubmitError = (err) => {
      setFormState('SUBMIT_ERROR');
      setSubmissionError(err);
    };
    if (formState === 'SUBMITTING') {
      resetTillVarianceSummaries();
      getTillVarianceSummaries(getLocalEndDateToUTC(endDate), getLocalStartDateToUTC(startDate))
        .then(handleSubmitSuccess)
        .catch((error) => handleSubmitError(error));
    }
  }, [currency, endDate, startDate, formState]);

  return (
    <>
      <article className="va-sm-t ta-sm-c ncss-col-sm-12 ncss-col-md-6 ncss-col-lg-4 p4-sm">
        <Subheader>{getLocalization('lbl.till-variance-summary')}</Subheader>
        <TillVarianceSummaryForm
          dateFormat={DATE_FORMAT}
          endDate={endDate}
          formState={formState}
          maxDate={maxDate}
          minDate={minDate}
          setEndDate={setEndDate}
          setStartDate={setStartDate}
          startDate={startDate}
          onSubmit={() => setFormState('SUBMITTING')}
        />
      </article>
      <br />
      <article className="va-sm-m ta-sm-c pb2-sm u-full-width">
        {match({ tenders, formState })(
          [
            () => formState === 'SUBMIT_SUCCESS' && tenders.length > 0,
            () => <TillVarianceSummaryTable tenders={tenders} />,
          ],
          [
            () => formState === 'SUBMIT_SUCCESS',
            () => (
              <article className="va-sm-t ta-sm-c ncss-col-sm-12 ncss-col-md-6 ncss-col-lg-4 p4-sm">
                <ErrorMessage>{getLocalization('msg.no-results-found')}</ErrorMessage>
              </article>
            ),
          ],
          [Default, () => null],
        )}
        {formState === 'SUBMIT_ERROR' ? (
          <article className="va-sm-t ta-sm-c ncss-col-sm-12 ncss-col-md-6 ncss-col-lg-4 p4-sm">
            <ErrorMessage rawError={submissionError}>
              {getLocalization('msg.an-error-occurred-please-try-again')}
            </ErrorMessage>
          </article>
        ) : null}
      </article>
    </>
  );
}

export default TillVarianceSummary;
